import React from 'react';
import { Battery, Bolt, Calendar, Sparkles, Percent, Receipt, MapPin, Calculator } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости электроэнергии
 */
const ResultsOverview = ({ results, formData, russianRegions }) => {
  if (!results) return null;

  // Найдем информацию о регионе
  const regionInfo = russianRegions.find(r => r.id === formData.region) || { name: 'Ваш регион' };

  // Форматирование даты расчета
  const calculationDateFormatted = new Date(results.calculationDate).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <ToolResult title="Результаты расчета стоимости электроэнергии">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/30 dark:to-amber-900/30 p-5 border border-yellow-100 dark:border-yellow-800/50 shadow-sm">
          <h3 className="text-base font-medium text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1.5">
            <Receipt className="w-4 h-4"/>
            Стоимость электроэнергии
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-amber-800 dark:text-amber-200" aria-live="polite">
              {formatCurrency(results.finalCost, 2)}
            </span>
            <span className="text-sm text-amber-600 dark:text-amber-400">
              за {results.totalConsumption.toFixed(1)} кВт⋅ч
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Регион: {regionInfo.name}
          </p>
        </div>

        {/* Информация о льготе (если есть) или экономии */}
        {results.discountInfo ? (
          <div className="rounded-lg bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 p-5 border border-purple-100 dark:border-purple-800/50 shadow-sm">
            <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4"/>
              Льгота {results.discountInfo.percent}%
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold text-purple-800 dark:text-purple-200" aria-live="polite">
                {formatCurrency(results.discountInfo.amount, 2)}
              </span>
              <span className="text-sm text-purple-600 dark:text-purple-400">
                экономия
              </span>
            </div>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
              {results.discountInfo.type}
            </p>
          </div>
        ) : results.savings > 0 ? (
          <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
            <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
              <Percent className="w-4 h-4"/>
              Экономия на многотарифном учете
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
                {formatCurrency(results.savings, 2)}
              </span>
              <span className="text-sm text-green-600 dark:text-green-400">
                {((results.savings / (results.costWithoutDiscount + results.savings)) * 100).toFixed(1)}%
              </span>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              По сравнению с однотарифным учетом
            </p>
          </div>
        ) : (
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
            <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
              <Battery className="w-4 h-4"/>
              Тариф в вашем регионе
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
                {formData.tariffType === 'single'
                  ? formatNumber(results.regionInfo.singleRate, 2)
                  : formData.tariffType === 'day_night'
                    ? `${formatNumber(results.regionInfo.dayRate, 2)}/${formatNumber(results.regionInfo.nightRate, 2)}`
                    : `${formatNumber(results.regionInfo.peakRate, 2)}/${formatNumber(results.regionInfo.semipeakRate, 2)}/${formatNumber(results.regionInfo.offpeakRate, 2)}`
                }
              </span>
              <span className="text-sm text-blue-600 dark:text-blue-400">
                ₽/кВт⋅ч
              </span>
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              {formData.tariffType === 'single'
                ? 'Одноставочный тариф'
                : formData.tariffType === 'day_night'
                  ? 'День/Ночь тариф'
                  : 'Трехзонный тариф'}
            </p>
          </div>
        )}
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Регион" 
          value={regionInfo.name} 
          color="indigo" 
          icon={<MapPin className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Тип тарифа" 
          value={formData.tariffType === 'single' 
            ? 'Одноставочный' 
            : formData.tariffType === 'day_night' 
              ? 'Двухзонный (день/ночь)' 
              : 'Трехзонный'}
          color="yellow" 
          icon={<Calculator className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Дата расчета" 
          value={calculationDateFormatted} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Потребление электроэнергии" 
          value={`${formatNumber(results.totalConsumption, 1)} кВт⋅ч`} 
          color="amber"
          icon={<Bolt className="w-4 h-4"/>} 
        />

        {formData.tariffType !== 'single' && (
          <div className="pl-6 border-l-2 border-amber-200 dark:border-amber-800">
            {formData.tariffType === 'day_night' ? (
              <>
                <ResultItem 
                  label="Дневное потребление (7:00-23:00)" 
                  value={`${formatNumber(results.consumption.day, 1)} кВт⋅ч`} 
                  size="sm"
                />
                <ResultItem 
                  label="Ночное потребление (23:00-7:00)" 
                  value={`${formatNumber(results.consumption.night, 1)} кВт⋅ч`} 
                  size="sm"
                />
              </>
            ) : (
              <>
                <ResultItem 
                  label="Пиковое потребление" 
                  value={`${formatNumber(results.consumption.peak, 1)} кВт⋅ч`} 
                  size="sm"
                />
                <ResultItem 
                  label="Полупиковое потребление" 
                  value={`${formatNumber(results.consumption.semipeak, 1)} кВт⋅ч`} 
                  size="sm"
                />
                <ResultItem 
                  label="Внепиковое потребление" 
                  value={`${formatNumber(results.consumption.offpeak, 1)} кВт⋅ч`} 
                  size="sm"
                />
              </>
            )}
          </div>
        )}
        
        <ResultItem 
          label="Стоимость без льгот" 
          value={formatCurrency(results.costWithoutDiscount, 2)} 
        />
        
        {results.discountInfo && (
          <ResultItem 
            label={`Льгота (${results.discountInfo.type})`} 
            value={`${formatCurrency(results.discountInfo.amount, 2)} (${results.discountInfo.percent}%)`} 
            color="purple" 
            icon={<Sparkles className="w-4 h-4"/>} 
          />
        )}

        {results.extraInfo?.household?.normExceeded && (
          <ResultItem 
            label="Превышение социальной нормы" 
            value={`${formatNumber(results.extraInfo.household.normExceedAmount, 1)} кВт⋅ч`} 
            color="red" 
          />
        )}
        
        {results.extraInfo?.household?.extraFees > 0 && (
          <ResultItem 
            label="Дополнительная плата за превышение нормы" 
            value={formatCurrency(results.extraInfo.household.extraFees, 2)} 
            color="red" 
          />
        )}
        
        {results.savings > 0 && (
          <ResultItem 
            label="Экономия на многотарифном учете" 
            value={formatCurrency(results.savings, 2)} 
            color="green" 
            icon={<Percent className="w-4 h-4"/>} 
          />
        )}

        <ResultItem 
          label="ИТОГОВАЯ СТОИМОСТЬ" 
          value={formatCurrency(results.finalCost, 2)} 
          color="amber"
          size="lg"
          icon={<Receipt className="w-4 h-4"/>} 
        />

        {results.extraInfo?.meterReadings && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mt-2 border border-blue-200 dark:border-blue-700">
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Данные по показаниям счетчика</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="text-blue-600 dark:text-blue-400">Предыдущие показания:</div>
              <div className="font-medium text-blue-700 dark:text-blue-300">{formatNumber(results.extraInfo.meterReadings.previous, 0)}</div>
              
              <div className="text-blue-600 dark:text-blue-400">Текущие показания:</div>
              <div className="font-medium text-blue-700 dark:text-blue-300">{formatNumber(results.extraInfo.meterReadings.current, 0)}</div>
              
              <div className="text-blue-600 dark:text-blue-400">Разница показаний:</div>
              <div className="font-medium text-blue-700 dark:text-blue-300">{formatNumber(results.extraInfo.meterReadings.difference, 0)} кВт⋅ч</div>
              
              {Math.abs(results.extraInfo.meterReadings.deviation) > 1 && (
                <>
                  <div className="text-blue-600 dark:text-blue-400">Расхождение с расчетом:</div>
                  <div className="font-medium text-blue-700 dark:text-blue-300">
                    {formatNumber(results.extraInfo.meterReadings.deviation, 0)} кВт⋅ч 
                    ({((results.extraInfo.meterReadings.deviation / results.extraInfo.meterReadings.difference) * 100).toFixed(1)}%)
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Информационная панель с тарифами */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mt-2 border border-amber-200 dark:border-amber-700">
        <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">Примененные тарифы для расчета</h4>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-2">
          {formData.tariffType === 'single' ? (
            <>
              <div className="text-amber-600 dark:text-amber-400">Одноставочный тариф:</div>
              <div className="font-medium text-amber-700 dark:text-amber-300">{formatNumber(results.regionInfo.singleRate, 2)} ₽/кВт⋅ч</div>
            </>
          ) : formData.tariffType === 'day_night' ? (
            <>
              <div className="text-amber-600 dark:text-amber-400">Дневной тариф (7:00-23:00):</div>
              <div className="font-medium text-amber-700 dark:text-amber-300">{formatNumber(results.regionInfo.dayRate, 2)} ₽/кВт⋅ч</div>
              
              <div className="text-amber-600 dark:text-amber-400">Ночной тариф (23:00-7:00):</div>
              <div className="font-medium text-amber-700 dark:text-amber-300">{formatNumber(results.regionInfo.nightRate, 2)} ₽/кВт⋅ч</div>
            </>
          ) : (
            <>
              <div className="text-amber-600 dark:text-amber-400">Пиковая зона:</div>
              <div className="font-medium text-amber-700 dark:text-amber-300">{formatNumber(results.regionInfo.peakRate, 2)} ₽/кВт⋅ч</div>
              
              <div className="text-amber-600 dark:text-amber-400">Полупиковая зона:</div>
              <div className="font-medium text-amber-700 dark:text-amber-300">{formatNumber(results.regionInfo.semipeakRate, 2)} ₽/кВт⋅ч</div>
              
              <div className="text-amber-600 dark:text-amber-400">Внепиковая зона:</div>
              <div className="font-medium text-amber-700 dark:text-amber-300">{formatNumber(results.regionInfo.offpeakRate, 2)} ₽/кВт⋅ч</div>
            </>
          )}
        </div>
        <p className="text-xs text-amber-600 dark:text-amber-400 italic">
          * Тарифы действительны на момент расчета и могут меняться в зависимости от решений региональных энергетических комиссий
        </p>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;